import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Grid, TextField, Button, Paper, CircularProgress, FormControl, InputLabel, MenuItem,
    Select, SelectChangeEvent, Typography, Chip, Slider, Alert, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tooltip, ToggleButton, ToggleButtonGroup,
    Accordion, AccordionSummary, AccordionDetails, Divider, Dialog, DialogTitle,
    DialogContent, DialogContentText, DialogActions
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import TerrainOutlinedIcon from "@mui/icons-material/TerrainOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Scatter } from "react-chartjs-2";
import {
    Chart as Chartjs, LinearScale, PointElement, LineElement, Title, Tooltip as ChartTooltip,
    Legend, Plugin
} from "chart.js";
import { format } from "date-fns";
import html2canvas from "html2canvas";
import { getActiveProjects } from "../../api/projectApi";
import { getUserProject } from "../../api/userApi";
import { getProjectActivities } from "../../api/activityApi";
import { profiles } from "../../contexts/constants/profiles";
import { Project } from "../../types/projectTypes";
import { ProjectActivity } from "../../types/ActivityTypes";
import {
    getTimeLocationChart, getAvailableChartImages, fetchChartImageObjectUrl, uploadChartImage,
    previewTowerKmz, importTowerKmz, deleteChartImage, getTowerCoordinateStatus,
    clearTowerCoordinates, downloadTowerKmz
} from "../../api/timeLocationChartApi";
import TimeLocationMap, { BaseLayerType } from "../../components/TimeLocationMap";
import {
    ActivitySeries, DateMode, KmzImportResult, ProjectChartImageType, TimeLocationChartData,
    TowerCoordinateStatus, seriesLegendLabel
} from "../../types/timeLocationChartTypes";

Chartjs.register(LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend);

/**
 * Paleta de la regla de negocio: cada actividad debe tener color único. Se combina con una
 * forma de punto distinta para que el diagrama siga siendo legible impreso en blanco y negro.
 */
const SERIES_COLORS = [
    '#d32f2f', '#1976d2', '#2e7d32', '#ed6c02', '#7b1fa2',
    '#00838f', '#c2185b', '#5d4037', '#455a64', '#f9a825'
];

const SERIES_SHAPES: ('circle' | 'rect' | 'triangle' | 'rectRot' | 'star' | 'cross')[] =
    ['circle', 'rect', 'triangle', 'rectRot', 'star', 'cross'];

/** Formato de fecha del informe. */
const DATE_FORMAT = 'dd-MM-yyyy';

/** Convierte una fecha ISO (yyyy-MM-dd) del backend al formato del informe. */
const formatIsoDate = (isoDate: string): string => {
    if (!isoDate) return '';
    return format(new Date(`${isoDate}T00:00:00`), DATE_FORMAT);
};

/**
 * Expresa una distancia en la notación de progresiva de obras lineales: 4.768 km se muestra
 * como 4+768, o sea kilómetro 4, metro 768.
 *
 * Se usa en lugar del número decimal porque en formato local el punto es separador de miles
 * y "4.768" se leería como 4.768 metros, un error de un factor de mil en un informe que sirve
 * para auditar fechas. Es además la notación que usa el cliente en su especificación.
 */
const formatChainage = (chainageKm: number): string => {
    if (chainageKm === null || chainageKm === undefined || isNaN(chainageKm)) return '';

    const kilometers = Math.floor(chainageKm);
    const meters = Math.round((chainageKm - kilometers) * 1000);

    // El redondeo puede dejar los metros en 1000; en ese caso sube el kilómetro.
    if (meters >= 1000) {
        return `${kilometers + 1}+000`;
    }
    return `${kilometers}+${meters.toString().padStart(3, '0')}`;
};

const TimeLocationChart: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<number>();
    const [projectActivities, setProjectActivities] = useState<ProjectActivity[]>([]);
    const [selectedActivityIds, setSelectedActivityIds] = useState<number[]>([]);
    // Actividades que se grafican por fecha de inicio. Las que no están acá usan la de término.
    const [startDateActivityIds, setStartDateActivityIds] = useState<number[]>([]);
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");

    const [chartData, setChartData] = useState<TimeLocationChartData | null>(null);

    // Series con línea poligonal activada. La regla pide que unir los puntos sea opcional.
    const [linedActivityIds, setLinedActivityIds] = useState<number[]>([]);

    const [availableImages, setAvailableImages] = useState<ProjectChartImageType[]>([]);

    // Importación de coordenadas desde KMZ, en dos pasos: vista previa y confirmación.
    const [kmzFile, setKmzFile] = useState<File | null>(null);
    const [kmzPreview, setKmzPreview] = useState<KmzImportResult | null>(null);
    // Cuántas torres tienen coordenada. Se consulta al elegir proyecto, sin generar el diagrama.
    const [coordinateStatus, setCoordinateStatus] = useState<TowerCoordinateStatus | null>(null);
    const [baseLayer, setBaseLayer] = useState<BaseLayerType>('SATELLITE');
    // Torre que el cursor está tocando en el diagrama, para resaltarla en el mapa.
    const [hoveredTower, setHoveredTower] = useState<string | null>(null);
    // Progresiva del punto bajo el cursor, para dibujar la línea guía vertical.
    const [hoveredChainage, setHoveredChainage] = useState<number | null>(null);

    // Alto del plano cartesiano. Es ajustable porque el tamaño útil depende de cuántas
    // actividades estén activas.
    // Alto compartido por el mapa y el plano cartesiano: ambos miden lo mismo, de modo que
    // al desplazarse por la zona de los dos mapas se vean mitad y mitad.
    const [panelHeight, setPanelHeight] = useState(380);
    // Los filtros se pliegan al generar: a partir de ahí interesa ver mapa y diagrama juntos.
    const [showControls, setShowControls] = useState(true);
    // Los datos del proyecto se configuran una vez, así que la sección arranca plegada.
    const [showProjectData, setShowProjectData] = useState(false);
    // Qué capa está esperando confirmación de borrado. Se confirma porque volver a tenerla
    // exige el archivo original, que no siempre está a mano.
    const [pendingRemoval, setPendingRemoval] = useState<'KMZ' | ProjectChartImageType | null>(null);

    // El perfil de elevación se descarga por apiClient y se expone como URL de objeto local.
    const [elevationUrl, setElevationUrl] = useState<string | null>(null);

    /**
     * Límites horizontales del área de dibujo del gráfico principal.
     *
     * El canvas no empieza en su borde izquierdo: antes están las etiquetas del eje temporal.
     * El perfil de elevación tiene que respetar esos mismos márgenes, porque si no, el
     * kilómetro 0 del perfil no cae bajo el kilómetro 0 del diagrama y leerlos juntos
     * llevaría a conclusiones equivocadas.
     */
    const [plotBounds, setPlotBounds] = useState<{ left: number; right: number; width: number } | null>(null);

    const exportRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<Chartjs<'scatter'> | null>(null);
    const hoveredChainageRef = useRef<number | null>(null);

    // react-chartjs-2 entrega el array de plugins solo al construir el gráfico, en un efecto
    // con dependencias vacías. Por eso el plugin no puede cerrar sobre el estado: leería
    // siempre el valor que tenía al montar. Se espeja en un ref lo que cambia en cada frame.
    useEffect(() => { hoveredChainageRef.current = hoveredChainage; }, [hoveredChainage]);

    useEffect(() => {
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Carga los proyectos visibles según el perfil del usuario.
     * Director, Gerente y Visualizador ven todos los activos; el resto solo los asignados.
     */
    const fetchProjects = async () => {
        try {
            setLoading(true);
            const userStr = localStorage.getItem("user");
            let role = "";
            let rut = "";
            if (userStr) {
                const user = JSON.parse(userStr);
                role = user.rols?.description;
                rut = user.identificationNum;
            }

            let projectsData: Project[] = [];
            if (role === profiles[0].name || role === profiles[2].name || role === profiles[3].name) {
                projectsData = await getActiveProjects();
            } else {
                const userProjects = await getUserProject(rut);
                projectsData = Array.isArray(userProjects) ? userProjects : [];
            }
            setProjects(projectsData.filter(p => p.status === undefined || p.status === true));
        } catch (error) {
            console.error("Error al obtener proyectos:", error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Descarga el perfil de elevación del proyecto y libera el anterior.
     */
    const loadChartImages = async (projectId: number) => {
        const elevation = await fetchChartImageObjectUrl(projectId, 'ELEVATION_PROFILE');
        setElevationUrl(previous => {
            if (previous) URL.revokeObjectURL(previous);
            return elevation;
        });
    };

    const handleProjectChange = async (event: SelectChangeEvent<number>) => {
        const projectId = Number(event.target.value);
        setSelectedProject(projectId);
        setChartData(null);
        setSelectedActivityIds([]);
        setStartDateActivityIds([]);
        setLinedActivityIds([]);
        setKmzPreview(null);
        setKmzFile(null);

        try {
            setLoading(true);
            const activities = await getProjectActivities(projectId);
            // Solo las actividades que reportan producción por torre generan fechas de término.
            setProjectActivities(activities.filter(pa => pa.activity?.addProduction));
            setAvailableImages(await getAvailableChartImages(projectId));
            setCoordinateStatus(await getTowerCoordinateStatus(projectId));
            await loadChartImages(projectId);
        } catch (error) {
            console.error("Error al cargar actividades del proyecto:", error);
            setProjectActivities([]);
        } finally {
            setLoading(false);
        }
    };

    const toggleActivity = (activityId: number) => {
        setSelectedActivityIds(prev => {
            if (!prev.includes(activityId)) return [...prev, activityId];
            // Al quitar la actividad se limpia también su modo de fecha.
            setStartDateActivityIds(modes => modes.filter(id => id !== activityId));
            return prev.filter(id => id !== activityId);
        });
    };

    /** Alterna entre graficar la fecha de inicio o la de término de una actividad. */
    const toggleDateMode = (activityId: number, mode: DateMode) => {
        setStartDateActivityIds(prev =>
            mode === 'START'
                ? (prev.includes(activityId) ? prev : [...prev, activityId])
                : prev.filter(id => id !== activityId)
        );
    };

    const toggleLine = (activityId: number) => {
        setLinedActivityIds(prev =>
            prev.includes(activityId) ? prev.filter(id => id !== activityId) : [...prev, activityId]
        );
    };

    const handleGenerate = async () => {
        if (!selectedProject || selectedActivityIds.length === 0) return;
        try {
            setLoading(true);
            setLoadError("");
            const data = await getTimeLocationChart(
                selectedProject,
                selectedActivityIds,
                startDateActivityIds,
                startDate || undefined,
                endDate || undefined
            );
            setChartData(data);
            // Por defecto todas las series salen unidas, que es la lectura habitual del diagrama.
            setLinedActivityIds(data.series.filter(s => s.points.length > 1).map(s => s.activityId));
            setShowControls(false);
        } catch (error) {
            console.error("Error al generar el diagrama:", error);
            setLoadError("No se pudo generar el diagrama.");
        } finally {
            setLoading(false);
        }
    };

    const handleUploadImage = async (imageType: ProjectChartImageType, file: File | null) => {
        if (!selectedProject || !file) return;
        try {
            setLoading(true);
            await uploadChartImage(selectedProject, imageType, file);
            setAvailableImages(await getAvailableChartImages(selectedProject));
            await loadChartImages(selectedProject);
        } catch (error) {
            console.error("Error al cargar la imagen:", error);
            setLoadError("No se pudo cargar la imagen.");
        } finally {
            setLoading(false);
        }
    };

    /** Paso 1: lee el KMZ y muestra qué torres quedarían emparejadas, sin escribir nada. */
    const handleKmzPreview = async (file: File | null) => {
        if (!selectedProject || !file) return;
        try {
            setLoading(true);
            setLoadError("");
            setKmzFile(file);
            setKmzPreview(await previewTowerKmz(selectedProject, file));
        } catch (error: any) {
            const backendMessage = error.response?.data?.mensaje || 'No se pudo leer el archivo KMZ.';
            setLoadError(backendMessage);
            setKmzPreview(null);
        } finally {
            setLoading(false);
        }
    };

    /** Paso 2: confirma y guarda las coordenadas. */
    /**
     * Descarga las coordenadas cargadas como .kmz.
     *
     * Sale en el mismo formato que espera la importacion, asi que sirve tanto para revisarlas en
     * Google Earth como para volver a cargarlas despues de corregirlas.
     */
    const handleDownloadKmz = async () => {
        if (!selectedProject) {
            return;
        }
        try {
            await downloadTowerKmz(selectedProject);
        } catch (error) {
            setLoadError("No se pudo descargar el KMZ de coordenadas.");
        }
    };

    const handleKmzImport = async () => {
        if (!selectedProject || !kmzFile) return;
        try {
            setLoading(true);
            const result = await importTowerKmz(selectedProject, kmzFile);
            setKmzPreview(result);
            setKmzFile(null);
            setCoordinateStatus(await getTowerCoordinateStatus(selectedProject));
            // El kilometraje cambia al pasar a geodésico, así que se regenera el diagrama.
            if (selectedActivityIds.length > 0) {
                await handleGenerate();
            }
        } catch (error: any) {
            const backendMessage = error.response?.data?.mensaje || 'No se pudieron importar las coordenadas.';
            setLoadError(backendMessage);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Elimina la capa que está esperando confirmación.
     *
     * Quitar el KMZ no borra torres: solo deja sus coordenadas en nulo, con lo que el
     * kilometraje vuelve a calcularse con la suma de vanos y el mapa deja de tener trazado.
     */
    const handleConfirmRemoval = async () => {
        if (!selectedProject || !pendingRemoval) return;
        const target = pendingRemoval;
        setPendingRemoval(null);

        try {
            setLoading(true);
            setLoadError("");

            if (target === 'KMZ') {
                setCoordinateStatus(await clearTowerCoordinates(selectedProject));
                setKmzPreview(null);
                setKmzFile(null);
            } else {
                await deleteChartImage(selectedProject, target);
                setAvailableImages(await getAvailableChartImages(selectedProject));
                await loadChartImages(selectedProject);
            }

            // El diagrama ya dibujado quedó con datos que dejaron de ser válidos.
            if (target === 'KMZ' && selectedActivityIds.length > 0) {
                await handleGenerate();
            }
        } catch (error: any) {
            const backendMessage = error.response?.data?.mensaje || 'No se pudo quitar el archivo.';
            setLoadError(backendMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        if (!exportRef.current) return;
        // useCORS hace que html2canvas vuelva a pedir las teselas del mapa como recurso con
        // CORS. Sin esto el canvas no puede leerlas y el mapa queda en gris.
        const canvas = await html2canvas(exportRef.current, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true
        });
        const link = document.createElement('a');
        link.download = 'Diagrama_Espacio_Tiempo.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    const hasElevationProfile = availableImages.includes('ELEVATION_PROFILE');
    const hasCoordinates = (coordinateStatus?.towersWithCoordinates ?? 0) > 0;
    const hasSatelliteMap = availableImages.includes('SATELLITE_MAP');

    /** Resumen de los filtros, para leerlos con la sección plegada. */
    const filterSummary = useMemo(() => {
        const parts: string[] = [];
        if (selectedActivityIds.length > 0) {
            parts.push(`${selectedActivityIds.length} actividad${selectedActivityIds.length === 1 ? '' : 'es'}`);
        }
        if (startDate && endDate) parts.push(`${formatIsoDate(startDate)} a ${formatIsoDate(endDate)}`);
        else if (startDate) parts.push(`desde ${formatIsoDate(startDate)}`);
        else if (endDate) parts.push(`hasta ${formatIsoDate(endDate)}`);
        return parts.join(' · ');
    }, [selectedActivityIds, startDate, endDate]);

    /**
     * Dibuja el mapa satelital detrás del plano cartesiano.
     *
     * El alto y ancho se ajustan al área del gráfico, de modo que el borde izquierdo del mapa
     * coincide con la primera torre y el derecho con la última, que es la alineación que pide
     * la especificación.
     */
    /**
     * Dibujos propios del plano: la línea guía vertical y la publicación de los márgenes del
     * área de dibujo, que el perfil de elevación necesita para alinearse.
     */
    const chartGuidesPlugin: Plugin<'scatter'> = useMemo(() => ({
        id: 'chartGuides',
        // afterLayout corre cuando cambia la geometría, no en cada frame, así que es el
        // momento correcto para publicar los márgenes del área de dibujo.
        afterLayout: (chart) => {
            const { left, right } = chart.chartArea;
            const width = chart.width;
            setPlotBounds(previous =>
                previous && previous.left === left && previous.right === right && previous.width === width
                    ? previous
                    : { left, right, width });
        },
        // Linea guia vertical sobre el punto que toca el cursor. Es lo que da la
        // correspondencia exacta entre diagrama y mapa, sin depender del estirado del fondo.
        afterDatasetsDraw: (chart) => {
            const chainage = hoveredChainageRef.current;
            if (chainage === null || chainage === undefined) return;

            const x = chart.scales.x.getPixelForValue(chainage);
            const area = chart.chartArea;
            const ctx = chart.ctx;

            ctx.save();
            ctx.beginPath();
            ctx.setLineDash([4, 4]);
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = '#004F86';
            ctx.moveTo(x, area.top);
            ctx.lineTo(x, area.bottom);
            ctx.stroke();
            ctx.restore();
        }
        // El plugin se construye una sola vez: todo lo que cambia se lee desde refs.
    }), []);

    // Cambiar la opacidad o terminar de cargar el mapa no altera datos ni opciones, así que
    // hay que pedir el redibujado a mano. El modo 'none' evita la animación.
    useEffect(() => {
        chartRef.current?.update('none');
    }, [hoveredChainage]);

    /** Datos en el formato que espera chart.js. */
    const scatterData = useMemo(() => {
        if (!chartData) return { datasets: [] };

        return {
            datasets: chartData.series
                .filter(series => series.points.length > 0)
                .map((series, index) => ({
                    label: seriesLegendLabel(series),
                    data: series.points.map(point => ({
                        x: point.chainageKm,
                        y: new Date(`${point.date}T00:00:00`).getTime(),
                        towerNumber: point.towerNumber
                    })),
                    backgroundColor: SERIES_COLORS[index % SERIES_COLORS.length],
                    borderColor: SERIES_COLORS[index % SERIES_COLORS.length],
                    pointStyle: SERIES_SHAPES[index % SERIES_SHAPES.length],
                    pointRadius: 3,
                    showLine: linedActivityIds.includes(series.activityId),
                    borderWidth: 1.5,
                    tension: 0
                }))
        };
    }, [chartData, linedActivityIds]);

    // Rango del eje espacial. Se fija con la longitud total para que la escala no cambie al
    // filtrar actividades.
    const spatialMax = chartData?.totalLengthKm ?? 0;

    /**
     * Posiciones de las marcas del eje espacial: una por torre, en su progresiva real.
     *
     * El eje sigue siendo numérico y sigue midiendo distancia; lo único que cambia es la
     * etiqueta. Esto importa: si las torres se repartieran a distancia igual, las pendientes
     * de las series dejarían de representar el ritmo de avance y los cruces se verían en un
     * lugar que no corresponde, porque los vanos no miden todos lo mismo.
     */
    const towerTicks = useMemo(
        () => (chartData?.towers ?? [])
            .map(tower => ({ value: tower.chainageKm, label: tower.towerNumber }))
            .sort((a, b) => a.value - b.value),
        [chartData]
    );

    /** Etiqueta de cada marca, buscada por la progresiva que chart.js devuelve. */
    const towerLabelByChainage = useMemo(() => {
        const labels = new Map<number, string>();
        towerTicks.forEach(tick => labels.set(tick.value, tick.label));
        return labels;
    }, [towerTicks]);

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        parsing: false as const,
        scales: {
            x: {
                type: 'linear' as const,
                min: 0,
                max: spatialMax > 0 ? spatialMax : undefined,
                title: { display: true, text: 'Torre', font: { size: 11 } },
                grid: { color: 'rgba(0,0,0,0.08)' },
                // Se reemplazan las marcas automáticas, que caen en kilómetros redondos, por una
                // marca en la progresiva exacta de cada torre. Así la línea de la grilla pasa
                // justo por la torre que rotula.
                afterBuildTicks: (axis: any) => {
                    if (towerTicks.length > 0) {
                        axis.ticks = towerTicks.map(tick => ({ value: tick.value }));
                    }
                },
                ticks: {
                    font: { size: 10 },
                    // Con muchas torres no caben todas las etiquetas: chart.js descarta las que
                    // se solapan y gira las restantes hasta 90 grados si hace falta.
                    autoSkip: true,
                    autoSkipPadding: 6,
                    maxRotation: 90,
                    callback: (value: any) => towerLabelByChainage.get(Number(value)) ?? ''
                }
            },
            y: {
                type: 'linear' as const,
                title: { display: true, text: 'Tiempo', font: { size: 11 } },
                grid: { color: 'rgba(0,0,0,0.08)' },
                ticks: {
                    font: { size: 10 },
                    // El eje se maneja en timestamps y se formatea acá, para no depender de un
                    // adaptador de fechas de chart.js.
                    callback: (value: any) => format(new Date(Number(value)), DATE_FORMAT)
                }
            }
        },
        // Al posar el cursor sobre un punto se resalta esa torre en el mapa de arriba.
        onHover: (_: any, elements: any[], chart: any) => {
            if (!elements || elements.length === 0) {
                setHoveredTower(null);
                setHoveredChainage(null);
                return;
            }
            const element = elements[0];
            const point = chart.data.datasets[element.datasetIndex]?.data[element.index];
            setHoveredTower(point?.towerNumber ?? null);
            setHoveredChainage(point?.x ?? null);
        },
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: { usePointStyle: true, boxWidth: 8, padding: 8, font: { size: 10 } }
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => {
                        const raw = context.raw;
                        const date = format(new Date(raw.y), DATE_FORMAT);
                        return `${context.dataset.label} — Torre ${raw.towerNumber} — km ${formatChainage(raw.x)} — ${date}`;
                    }
                }
            }
        }
    }), [spatialMax, towerTicks, towerLabelByChainage]);

    return (
        <Box sx={{ p: 2 }}>
            {loading && <CircularProgress />}

            {/* Barra superior: identidad de la pantalla, el proyecto del que depende todo lo
                demás y la exportación. Queda siempre visible, incluso con las secciones
                plegadas, porque exportar se hace justamente cuando ya no hay filtros a la vista. */}
            <Paper sx={{ p: 1.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", color: "#004F86" }}>
                    Diagrama Espacio-Tiempo
                </Typography>
                <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel>Seleccionar Proyecto</InputLabel>
                    <Select label="Seleccionar Proyecto" value={selectedProject || ""} onChange={handleProjectChange}>
                        {projects.map(project => (
                            <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" variant="contained" color="primary" onClick={handleExport} disabled={!chartData}>
                    Exportar imagen
                </Button>
            </Paper>

            {/* Seccion 1: lo que define la consulta. Se pliega al generar, que es cuando pasa a
                interesar el alto para ver mapa y diagrama juntos. */}
            <Accordion
                expanded={showControls}
                onChange={() => setShowControls(prev => !prev)}
                disableGutters
                sx={{ mb: 0.5 }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#004F86' }}>
                        Filtros del diagrama
                    </Typography>
                    {!showControls && filterSummary && (
                        <Typography variant="caption" color="textSecondary" sx={{ ml: 1.5, alignSelf: 'center' }}>
                            {filterSummary}
                        </Typography>
                    )}
                </AccordionSummary>
                <AccordionDetails>
            <Grid container spacing={1.5} sx={{ mb: 1 }}>
                <Grid item xs={6} md={3}>
                    <TextField label="Fecha de Inicio" type="date" value={startDate} size="small"
                        onChange={(e) => setStartDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <TextField label="Fecha de Término" type="date" value={endDate} size="small"
                        onChange={(e) => setEndDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
                </Grid>
            </Grid>

            {/* Selección de actividades: cada una será una serie con color y forma propios */}
            {projectActivities.length > 0 && (
                <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#004F86', display: 'block', mb: 0.5 }}>
                        Actividades a graficar
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {projectActivities.map((pa, index) => {
                            const activityId = pa.activity?.id as number;
                            const selected = selectedActivityIds.includes(activityId);
                            const color = SERIES_COLORS[selectedActivityIds.indexOf(activityId) % SERIES_COLORS.length];
                            const label = pa.activity?.subActivity
                                ? `${pa.activity?.name} - ${pa.activity?.subActivity}`
                                : pa.activity?.name;
                            return (
                                <Chip
                                    key={`${activityId}-${index}`}
                                    size="small"
                                    label={label}
                                    onClick={() => toggleActivity(activityId)}
                                    variant={selected ? 'filled' : 'outlined'}
                                    sx={{
                                        cursor: 'pointer',
                                        backgroundColor: selected ? color : 'white',
                                        color: selected ? 'white' : '#616161',
                                        borderColor: selected ? color : '#d0d0d0',
                                        fontWeight: selected ? 'bold' : 'normal'
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* Qué fecha grafica cada actividad. Se elige antes de generar porque cambia la
                consulta al backend, no solo el dibujo. */}
            {selectedActivityIds.length > 0 && (
                <Box sx={{ mb: 1, p: 1, backgroundColor: '#f5f7fa', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#004F86', display: 'block', mb: 0.5 }}>
                        Fecha a graficar por actividad
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {selectedActivityIds.map(activityId => {
                            const activity = projectActivities.find(pa => pa.activity?.id === activityId)?.activity;
                            const label = activity?.subActivity
                                ? `${activity?.name} - ${activity?.subActivity}`
                                : activity?.name;
                            const isStart = startDateActivityIds.includes(activityId);
                            return (
                                <Box key={activityId} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography variant="caption">{label}</Typography>
                                    <ToggleButtonGroup
                                        size="small"
                                        exclusive
                                        value={isStart ? 'START' : 'COMPLETION'}
                                        onChange={(_, value) => value && toggleDateMode(activityId, value as DateMode)}
                                    >
                                        <ToggleButton value="START">Inicio</ToggleButton>
                                        <ToggleButton value="COMPLETION">Término</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            );
                        })}
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                        Inicio: primer reporte diario de la torre en esa actividad, el mismo criterio
                        que usa Control de Producción. Término: día en que la torre llegó al 100%.
                    </Typography>
                </Box>
            )}

            <Box sx={{ mt: 0.5 }}>
                <Button size="small" variant="contained" color="primary" onClick={handleGenerate}
                    disabled={!selectedProject || selectedActivityIds.length === 0}>
                    Generar diagrama
                </Button>
            </Box>
                </AccordionDetails>
            </Accordion>

            {/* Seccion 2: archivos que se cargan una vez por proyecto, no en cada consulta.
                Por eso van aparte de los filtros y arrancan plegados. */}
            <Accordion
                expanded={showProjectData}
                onChange={() => setShowProjectData(prev => !prev)}
                disableGutters
                sx={{ mb: 1.5 }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#004F86' }}>
                        Datos del proyecto
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 1.5, alignSelf: 'center' }}>
                        {hasCoordinates ? 'KMZ cargado' : 'sin KMZ'}
                        {' · '}
                        {hasElevationProfile ? 'perfil de elevación cargado' : 'sin perfil de elevación'}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {!selectedProject && (
                        <Typography variant="body2" color="textSecondary">
                            Seleccione un proyecto para administrar sus archivos.
                        </Typography>
                    )}

                    {selectedProject && (
                        <>
                            {/* Coordenadas: alimentan el mapa y el kilometraje geodésico. */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 1 }}>
                                <PlaceOutlinedIcon sx={{ color: hasCoordinates ? '#2e7d32' : '#bdbdbd' }} />
                                <Box sx={{ flexGrow: 1, minWidth: 260 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        Coordenadas de torres (KMZ)
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {coordinateStatus
                                            ? hasCoordinates
                                                ? `${coordinateStatus.towersWithCoordinates} de ${coordinateStatus.totalTowers} torres localizadas. El kilometraje se calcula por distancia geodésica.`
                                                : `Sin coordenadas. El kilometraje se calcula sumando los vanos del mantenedor de torres.`
                                            : 'Sin información'}
                                    </Typography>
                                </Box>
                                <Button size="small" variant="outlined" component="label">
                                    {hasCoordinates ? 'Reemplazar' : 'Importar KMZ'}
                                    <input hidden accept=".kmz,.kml" type="file"
                                        onChange={(e) => handleKmzPreview(e.target.files?.[0] || null)} />
                                </Button>
                                <Button size="small" variant="outlined"
                                    disabled={!hasCoordinates}
                                    onClick={handleDownloadKmz}>
                                    Descargar
                                </Button>
                                <Button size="small" color="error" startIcon={<DeleteOutlineIcon />}
                                    disabled={!hasCoordinates}
                                    onClick={() => setPendingRemoval('KMZ')}>
                                    Quitar
                                </Button>
                            </Box>

                            <Divider />

                            {/* Perfil de elevación: imagen que se muestra bajo el diagrama. */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 1 }}>
                                <TerrainOutlinedIcon sx={{ color: hasElevationProfile ? '#2e7d32' : '#bdbdbd' }} />
                                <Box sx={{ flexGrow: 1, minWidth: 260 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        Perfil de elevación
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {hasElevationProfile
                                            ? 'Imagen cargada. Se muestra bajo el diagrama, alineada con el eje espacial.'
                                            : 'Imagen opcional que se muestra bajo el diagrama.'}
                                    </Typography>
                                </Box>
                                {elevationUrl && (
                                    <Box component="img" src={elevationUrl} alt="Perfil de elevación"
                                        sx={{ height: 34, border: '1px solid #e0e0e0', borderRadius: 0.5 }} />
                                )}
                                <Button size="small" variant="outlined" component="label">
                                    {hasElevationProfile ? 'Reemplazar' : 'Cargar imagen'}
                                    <input hidden accept="image/*" type="file"
                                        onChange={(e) => handleUploadImage('ELEVATION_PROFILE', e.target.files?.[0] || null)} />
                                </Button>
                                <Button size="small" color="error" startIcon={<DeleteOutlineIcon />}
                                    disabled={!hasElevationProfile}
                                    onClick={() => setPendingRemoval('ELEVATION_PROFILE')}>
                                    Quitar
                                </Button>
                            </Box>

                            {/* El mapa satelital se usaba como fondo del diagrama, función que se
                                retiró. La fila aparece solo si el proyecto todavía tiene la imagen
                                guardada, para poder darla de baja. */}
                            {hasSatelliteMap && (
                                <>
                                    <Divider />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 1 }}>
                                        <TerrainOutlinedIcon sx={{ color: '#bdbdbd' }} />
                                        <Box sx={{ flexGrow: 1, minWidth: 260 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                Mapa satelital
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Imagen de una versión anterior del diagrama. Ya no se usa y puede eliminarse.
                                            </Typography>
                                        </Box>
                                        <Button size="small" color="error" startIcon={<DeleteOutlineIcon />}
                                            onClick={() => setPendingRemoval('SATELLITE_MAP')}>
                                            Quitar
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </>
                    )}
                </AccordionDetails>
            </Accordion>

            {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

            {/* Vista previa del KMZ antes de escribir coordenadas. El emparejamiento es por
                nombre normalizado, así que conviene que el usuario lo revise. */}
            {kmzPreview && (
                <Alert
                    severity={kmzPreview.applied ? 'success' : 'info'}
                    sx={{ mb: 2 }}
                    action={!kmzPreview.applied && kmzPreview.matched.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" onClick={() => { setKmzPreview(null); setKmzFile(null); }}>
                                Cancelar
                            </Button>
                            <Button size="small" variant="contained" onClick={handleKmzImport}>
                                Confirmar importación
                            </Button>
                        </Box>
                    ) : undefined}
                >
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {kmzPreview.applied
                            ? `Se importaron las coordenadas de ${kmzPreview.matched.length} torres.`
                            : `El archivo tiene ${kmzPreview.pointsInFile} puntos y calzan ${kmzPreview.matched.length} de las ${kmzPreview.towersInProject} torres del proyecto.`}
                    </Typography>
                    {kmzPreview.towersWithoutPoint.length > 0 && (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                            Sin coordenada ({kmzPreview.towersWithoutPoint.length}): {kmzPreview.towersWithoutPoint.join(', ')}
                        </Typography>
                    )}
                    {kmzPreview.pointsWithoutTower.length > 0 && (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                            Puntos del archivo sin torre en el proyecto ({kmzPreview.pointsWithoutTower.length}): {kmzPreview.pointsWithoutTower.join(', ')}
                        </Typography>
                    )}
                </Alert>
            )}

            {chartData && (
                <>
                    {/* Barra de vista: ajustes que solo cambian el dibujo, no la consulta. Va
                        fuera de las secciones plegables porque se usa con el diagrama a la
                        vista, que es cuando los filtros ya están ocultos. */}
                    <Paper sx={{ p: 1, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        {chartData.series.some(s => s.points.length > 1) && (
                            <>
                                <Typography variant="caption" color="textSecondary">
                                    Unir puntos:
                                </Typography>
                                {chartData.series.filter(s => s.points.length > 1).map((series: ActivitySeries) => (
                                    <Chip
                                        key={series.activityId}
                                        size="small"
                                        label={seriesLegendLabel(series)}
                                        onClick={() => toggleLine(series.activityId)}
                                        variant={linedActivityIds.includes(series.activityId) ? 'filled' : 'outlined'}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                ))}
                            </>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <Typography variant="caption" color="textSecondary">Alto paneles</Typography>
                        <Slider size="small" value={panelHeight} min={220} max={900} step={20}
                            valueLabelDisplay="auto" sx={{ width: 160 }}
                            onChange={(_, value) => setPanelHeight(value as number)} />
                    </Paper>

                    <Box ref={exportRef} sx={{ backgroundColor: 'white', p: 1 }}>
                        {/* Banda superior: el trazado real sobre el mapa. Comparte el trazado con
                            el diagrama, no los ejes, así que se sincronizan resaltando la torre. */}
                        <Paper sx={{ p: 1.5, mb: 0.5 }}>
                            <TimeLocationMap
                                towers={chartData.towers}
                                highlightedTowerNumber={hoveredTower}
                                baseLayer={baseLayer}
                                onBaseLayerChange={setBaseLayer}
                                height={panelHeight}
                            />
                        </Paper>

                        <Paper sx={{ p: 1.5, height: panelHeight }}>
                            <Scatter
                                ref={chartRef}
                                data={scatterData}
                                options={chartOptions as any}
                                plugins={[chartGuidesPlugin]}
                            />
                        </Paper>

                        {/* Sub-gráfico inferior: perfil de elevación entregado como imagen.
                            Se le aplica el mismo padding lateral que tiene el área de dibujo del
                            gráfico principal, para que ambos compartan la escala de kilometraje y
                            se puedan leer en vertical uno contra otro. */}
                        {elevationUrl && (
                            <Paper sx={{ mt: 1, p: 2, pt: 1 }}>
                                <Typography variant="caption" color="textSecondary">Perfil de elevación</Typography>
                                <Box
                                    sx={{
                                        pl: plotBounds ? `${plotBounds.left}px` : 0,
                                        pr: plotBounds ? `${plotBounds.width - plotBounds.right}px` : 0
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={elevationUrl}
                                        alt="Perfil de elevación"
                                        sx={{ width: '100%', display: 'block' }}
                                    />
                                </Box>
                            </Paper>
                        )}
                    </Box>

                    {/* Resumen del eje espacial y torres que quedaron fuera */}
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 2 }}>
                        {/* Las longitudes son distancias, no progresivas, así que van en
                            kilómetros con separador decimal local. Se muestran las dos para poder
                            contrastar: miden extensiones distintas, porque los vanos incluyen
                            tramos de torres que no tienen coordenada en el KMZ. */}
                        <Typography variant="body2">
                            Longitud de la línea:{' '}
                            <strong>{chartData.totalLengthKm?.toLocaleString('es-CL')} km</strong>
                            {chartData.chainageSource === 'GEODESIC' ? ' (geodésica, desde KMZ)' : ' (suma de vanos)'}
                        </Typography>
                        {chartData.chainageSource === 'GEODESIC' && (
                            <Tooltip title="Dato del mantenedor de torres, mostrado como referencia. Difiere de la geodésica porque incluye tramos de torres sin coordenada en el KMZ.">
                                <Typography variant="body2" sx={{ color: 'text.secondary', cursor: 'help' }}>
                                    Según suma de vanos: <strong>{chartData.totalSpanLengthKm?.toLocaleString('es-CL')} km</strong>
                                </Typography>
                            </Tooltip>
                        )}
                        <Typography variant="body2">
                            Torres en el eje: <strong>{chartData.towers.length}</strong>
                        </Typography>
                        {chartData.excludedTowers.length > 0 && (
                            <Tooltip title={chartData.excludedTowers.join(', ')}>
                                <Typography variant="body2" sx={{ color: '#8a6d00', cursor: 'help' }}>
                                    {chartData.excludedTowers.length} torres sin vano quedaron fuera del eje
                                </Typography>
                            </Tooltip>
                        )}
                    </Box>

                    {/* Alerta de solapamiento: cruces entre líneas de actividad */}
                    {chartData.crossings.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 1 }}>
                                Se detectaron {chartData.crossings.length} cruces entre actividades. Un cruce
                                significa que en ese tramo el orden de las fechas se invierte, lo que indica un
                                error o desfase en las fechas registradas.
                            </Alert>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>Actividad A</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>Actividad B</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>Tramo</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>Progresiva (km+m)</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>Fecha</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {chartData.crossings.map((crossing, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{crossing.activityAName}</TableCell>
                                                <TableCell>{crossing.activityBName}</TableCell>
                                                <TableCell>{crossing.fromTowerNumber} → {crossing.toTowerNumber}</TableCell>
                                                <TableCell>{formatChainage(crossing.chainageKm)}</TableCell>
                                                <TableCell>{formatIsoDate(crossing.date)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </>
            )}

            {/* Confirmación de borrado. Se pide porque recuperar la capa exige tener a mano el
                archivo original, que en el caso del KMZ lo entrega el cliente. */}
            <Dialog open={pendingRemoval !== null} onClose={() => setPendingRemoval(null)}>
                <DialogTitle>
                    {pendingRemoval === 'KMZ' && 'Quitar coordenadas del proyecto'}
                    {pendingRemoval === 'ELEVATION_PROFILE' && 'Quitar perfil de elevación'}
                    {pendingRemoval === 'SATELLITE_MAP' && 'Quitar mapa satelital'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText component="div">
                        {pendingRemoval === 'KMZ' ? (
                            <>
                                Se borrarán la latitud y longitud de las{' '}
                                <strong>{coordinateStatus?.towersWithCoordinates ?? 0} torres</strong> localizadas.
                                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                                    <li>Las torres y sus reportes diarios no se tocan.</li>
                                    <li>El mapa del trazado deja de mostrarse.</li>
                                    <li>El kilometraje vuelve a calcularse sumando los vanos del mantenedor.</li>
                                </Box>
                            </>
                        ) : pendingRemoval === 'SATELLITE_MAP' ? (
                            'Se eliminará la imagen del mapa satelital. El diagrama ya no la utiliza, así que nada cambia en pantalla.'
                        ) : (
                            'Se eliminará la imagen del perfil de elevación de este proyecto. El diagrama y el mapa no cambian.'
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingRemoval(null)}>Cancelar</Button>
                    <Button color="error" variant="contained" onClick={handleConfirmRemoval}>
                        Quitar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TimeLocationChart;
